export const PENDING_SIGN_IN_KEY = 'passThePushups.pendingSignIn';
export const PENDING_SIGN_IN_MAX_AGE_MS = 60 * 60 * 1000;
export const SIGN_IN_RESEND_COOLDOWN_MS = 60 * 1000;

export interface EmailSignInState {
  phase: 'email-entry' | 'sending' | 'code-entry' | 'verifying';
  email: string;
  lastSentAt: number | null;
  redirectTo: string | null;
  error: string | null;
}

interface AuthResult { error: unknown }

export interface EmailSignInAuth {
  signInWithOtp(input: { email: string; options: { emailRedirectTo: string } }): Promise<AuthResult>;
  verifyOtp(input: { email: string; token: string; type: 'email' }): Promise<AuthResult>;
}

export interface SignInStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

interface EmailSignInDependencies {
  auth: EmailSignInAuth;
  storage: () => SignInStorage;
  now: () => number;
}

export function initialEmailSignInState(): EmailSignInState {
  return { phase: 'email-entry', email: '', lastSentAt: null, redirectTo: null, error: null };
}

export function isSignInBusy(state: EmailSignInState): boolean {
  return state.phase === 'sending' || state.phase === 'verifying';
}

export function resendSecondsRemaining(state: EmailSignInState, now: number): number {
  return state.lastSentAt === null ? 0
    : Math.max(0, Math.ceil((state.lastSentAt + SIGN_IN_RESEND_COOLDOWN_MS - now) / 1000));
}

function signInError(error: unknown, verifying: boolean): string {
  const details = error as { code?: string; status?: number } | null;
  if (details?.status === 429 || details?.code?.startsWith('over_')) {
    return 'Too many attempts. Please wait a little before trying again.';
  }
  if (verifying && (details?.code === 'otp_expired' || details?.status === 403)) {
    return 'That code is invalid, expired, or already used. Try the latest email, or resend it.';
  }
  // Never display provider messages, which can contain credentials or other raw input.
  return verifying
    ? 'Could not verify the code. Check the code and your connection, then try again.'
    : 'Could not send the email. Check your email address and connection, then try again.';
}

export class EmailSignInClient {
  private state = initialEmailSignInState();
  private generation = 0;
  private listeners = new Set<() => void>();

  constructor(private readonly dependencies: EmailSignInDependencies) {
    try {
      const stored: unknown = JSON.parse(dependencies.storage().getItem(PENDING_SIGN_IN_KEY) ?? 'null');
      if (stored && typeof stored === 'object') {
        const pending = stored as Partial<EmailSignInState>;
        const age = dependencies.now() - Number(pending.lastSentAt);
        if (typeof pending.email === 'string' && pending.email.trim()
          && pending.email.length <= 320 && typeof pending.lastSentAt === 'number'
          && Number.isFinite(pending.lastSentAt) && age >= 0 && age < PENDING_SIGN_IN_MAX_AGE_MS
          && typeof pending.redirectTo === 'string') {
          this.state = {
            phase: 'code-entry', email: pending.email, lastSentAt: pending.lastSentAt,
            redirectTo: pending.redirectTo, error: null,
          };
          return;
        }
      }
      this.removePending();
    } catch {
      this.removePending();
    }
  }

  getState(): EmailSignInState { return { ...this.state }; }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => { this.listeners.delete(listener); };
  }

  private emit(): void { this.listeners.forEach((listener) => listener()); }

  private removePending(): void {
    try { this.dependencies.storage().removeItem(PENDING_SIGN_IN_KEY); } catch { /* In-memory fallback. */ }
  }

  private savePending(): void {
    const { email, lastSentAt, redirectTo } = this.state;
    try {
      this.dependencies.storage().setItem(PENDING_SIGN_IN_KEY, JSON.stringify({ email, lastSentAt, redirectTo }));
    } catch { /* In-memory fallback. Never store the code. */ }
  }

  setEmailDraft(email: string): void {
    if (this.state.phase === 'email-entry') this.state.email = email;
  }

  /** Also invalidates outstanding sends/verifications when auth succeeds via a link. */
  clear(): void {
    this.generation += 1;
    this.state = initialEmailSignInState();
    this.removePending();
    this.emit();
  }

  useDifferentEmail(): void {
    if (!isSignInBusy(this.state)) this.clear();
  }

  /** Refresh the cooldown without ever submitting or resending on app visibility. */
  refresh(): void {
    if (!isSignInBusy(this.state) && this.state.lastSentAt !== null
      && this.dependencies.now() - this.state.lastSentAt >= PENDING_SIGN_IN_MAX_AGE_MS) {
      this.clear();
    } else {
      this.emit();
    }
  }

  async send(email: string, redirectTo: string): Promise<void> {
    if (isSignInBusy(this.state)) return;
    const resending = this.state.lastSentAt !== null;
    if (resending && resendSecondsRemaining(this.state, this.dependencies.now()) > 0) return;
    const destination = resending ? this.state.email : email.trim();
    if (!destination) return;
    // A resend belongs to the original request, even if navigation changed meanwhile.
    const redirect = resending ? this.state.redirectTo! : redirectTo;
    const generation = ++this.generation;
    this.state = { ...this.state, phase: 'sending', email: destination, redirectTo: redirect, error: null };
    this.emit();
    try {
      const { error } = await this.dependencies.auth.signInWithOtp({
        email: destination, options: { emailRedirectTo: redirect },
      });
      if (generation !== this.generation) return;
      if (error) throw error;
      this.state = { ...this.state, phase: 'code-entry', lastSentAt: this.dependencies.now() };
      this.savePending();
    } catch (error) {
      if (generation !== this.generation) return;
      this.state = { ...this.state, phase: resending ? 'code-entry' : 'email-entry', error: signInError(error, false) };
    }
    this.emit();
  }

  async verify(code: string): Promise<void> {
    if (isSignInBusy(this.state) || this.state.lastSentAt === null) return;
    const token = code.trim();
    if (!/^\d+$/.test(token)) {
      this.state.error = 'Enter the numeric code from your email.';
      this.emit();
      return;
    }
    const generation = ++this.generation;
    this.state = { ...this.state, phase: 'verifying', error: null };
    this.emit();
    try {
      const { error } = await this.dependencies.auth.verifyOtp({ email: this.state.email, token, type: 'email' });
      if (generation !== this.generation) return;
      if (error) throw error;
      // Supabase's existing auth callback owns session setup and signed-in navigation.
      this.clear();
    } catch (error) {
      if (generation !== this.generation) return;
      this.state = { ...this.state, phase: 'code-entry', error: signInError(error, true) };
      this.emit();
    }
  }
}
